/**
 * Which system the person reading the downloads page is on.
 *
 * Since 2026-10-06 Airlock ships for macOS only — the owner stopped the Windows
 * and Linux builds — so the answer no longer picks a file: every download is the
 * macOS one. It decides one sentence instead, `OTHER_SYSTEMS`, said beside the
 * button to somebody on Windows or Linux. Only the browser can know: the account
 * app is served as static files and the Worker behind it sees a user agent it
 * has no business reading for this. There is no server round trip and no
 * hydration to match, so it is read once, at first render.
 *
 * The site makes the same guess for the same reason (`site/src/lib/platform.ts`)
 * and the two are deliberately separate copies — they are different builds, and
 * a shared module between them would be a package to publish for forty lines.
 */

export type Platform = 'mac' | 'win' | 'linux';

/** Said beside a download to somebody whose browser is on Windows or Linux. */
export const OTHER_SYSTEMS = 'Airlock runs on macOS (Apple silicon) for now.';

/** The three things a browser may say about where it runs, any of them absent. */
export interface Agent {
  userAgentData?: { platform?: string };
  platform?: string;
  userAgent?: string;
}

/**
 * The system a browser says it is on.
 *
 * A phone, a tablet and a Chromebook get the default and no sentence: somebody
 * opening their account on a phone is as likely to be installing on a Mac as on
 * anything else. Checked before Linux, because Android
 * and ChromeOS both say Linux in their user agent.
 */
export function platformOf(agent: Agent): Platform {
  const said = [agent.userAgentData?.platform, agent.platform, agent.userAgent].filter(Boolean).join(' ');
  if (/Android|iPhone|iPad|iPod|CrOS|Chrome OS/.test(said)) return 'mac';
  if (/\bWin/.test(said)) return 'win';
  if (/\bLinux\b|\bX11\b/.test(said)) return 'linux';
  return 'mac';
}

/** The visitor's system. Read from the live browser, or the default without one. */
export function platform(): Platform {
  return typeof navigator === 'undefined' ? 'mac' : platformOf(navigator as Agent);
}
